import { serializeError } from "serialize-error";
import { createApi } from "@reduxjs/toolkit/query/react";
import { supabase, supabaseBaseQuery, type Database } from "../../../supabase";
import type { NewPrompt } from "../../resources/forms/NewPromptForm";
import type { PromptMeta, PromptTestCase } from "../../../types/Resource";
import type { GetPromptVersionsResponse, GetPromptVersionsPayload } from "../types/promptApiTypes";
import { resourcesApi } from "../../resources/api/resourcesApi";

const promptApi = createApi({
  reducerPath: "api.Prompts",
  baseQuery: supabaseBaseQuery,
  tagTypes: ["Prompt", "PromptVersions"],
  endpoints: (build) => ({
    getPrompt: build.query<
      Database["public"]["Views"]["user_full_prompts"]["Row"] | null,
      { promptId: string }
    >({
      queryFn: async ({ promptId }) => {
        const { data, error } = await supabase
          .from("user_full_prompts")
          .select("*")
          .eq("id", promptId)
          .single();

        if (error) return { error: serializeError(error) };

        return { data };
      },

      providesTags: (_, __, { promptId }) => [{ type: "Prompt", id: promptId }],
    }),

    createPrompt: build.mutation<
      string,
      {
        prompt: NewPrompt;
      }
    >({
      queryFn: async ({ prompt }) => {
        const { data: newPromptId, error } = await supabase.rpc("create_prompt", {
          p_name: prompt.name,
          p_parent_id: prompt.parent_id || undefined,
          p_icon: prompt.icon || undefined,
          p_meta: prompt.meta || undefined,
          p_test_cases: prompt.test_cases || [],
        });

        if (error) return { error: serializeError(error) };

        return { data: newPromptId };
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(resourcesApi.util.invalidateTags([{ type: "Resources" }]));
        } catch {
          // Ignore errors
        }
      },
    }),

    copyPrompt: build.mutation<string, { promptId: string; name?: string }>({
      queryFn: async ({ promptId, name }) => {
        const { data: existingPrompt, error: fetchError } = await supabase
          .from("user_full_prompts")
          .select("*")
          .eq("id", promptId)
          .single();

        if (fetchError) return { error: serializeError(fetchError) };

        const { name: currentName, parent_id, icon, meta, test_cases } = existingPrompt;

        const { data: newPromptId, error: createError } = await supabase.rpc("create_prompt", {
          p_name: name || currentName!,
          p_parent_id: parent_id || undefined,
          p_icon: icon || undefined,
          p_meta: meta,
          p_test_cases: test_cases,
        });

        if (createError) return { error: serializeError(createError) };

        return { data: newPromptId };
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(resourcesApi.util.invalidateTags([{ type: "Resources" }]));
        } catch {
          // Ignore errors
        }
      },
    }),

    createPromptVersion: build.mutation<
      Database["public"]["Functions"]["create_prompt_version"]["Returns"],
      {
        promptId: string;
        meta: PromptMeta;
        versionName?: string;
        versionDescription?: string;
        setCurrent?: boolean;
      }
    >({
      queryFn: async ({ promptId, meta, versionName, versionDescription, setCurrent = true }) => {
        const { data, error } = await supabase.rpc("create_prompt_version", {
          p_prompt_id: promptId,
          p_meta: meta,
          p_name: versionName,
          p_description: versionDescription,
          p_set_current: setCurrent,
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },

      invalidatesTags: (_, __, { promptId }) => [
        { type: "Prompt", id: promptId },
        { type: "PromptVersions", id: promptId },
      ],
      onQueryStarted: async ({ promptId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(resourcesApi.util.invalidateTags([{ type: "Resources", id: promptId }]));
        } catch {
          // Ignore errors
        }
      },
    }),

    // Atomic "Save": commits both the prompt version and the dataset (test data)
    // version in a single transaction, moving both current-version pointers.
    savePromptVersionWithData: build.mutation<
      Database["public"]["Functions"]["save_prompt_version_with_data"]["Returns"],
      {
        promptId: string;
        meta: PromptMeta;
        test_cases: PromptTestCase[];
        versionName?: string;
        versionDescription?: string;
      }
    >({
      queryFn: async ({ promptId, meta, test_cases, versionName, versionDescription }) => {
        const { data, error } = await supabase.rpc("save_prompt_version_with_data", {
          p_prompt_id: promptId,
          p_meta: meta,
          p_rows: test_cases,
          p_version_name: versionName,
          p_version_description: versionDescription,
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },

      invalidatesTags: (_, __, { promptId }) => [
        { type: "Prompt", id: promptId },
        { type: "PromptVersions", id: promptId },
      ],
      onQueryStarted: async ({ promptId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(resourcesApi.util.invalidateTags([{ type: "Resources", id: promptId }]));
        } catch {
          // Ignore errors
        }
      },
    }),

    getPromptVersions: build.query<GetPromptVersionsResponse, GetPromptVersionsPayload>({
      queryFn: async ({ promptId }) => {
        const { data: savedVersions, error } = await supabase.rpc("get_prompt_versions", {
          p_prompt_id: promptId,
        });

        if (error) return { error: serializeError(error) };

        return { data: savedVersions as unknown as GetPromptVersionsResponse };
      },

      providesTags: (_, __, { promptId }) => [{ type: "PromptVersions", id: promptId }],
    }),
  }),
});

const {
  useGetPromptQuery,
  useCreatePromptMutation,
  useCopyPromptMutation,
  useCreatePromptVersionMutation,
  useSavePromptVersionWithDataMutation,
  useGetPromptVersionsQuery,
} = promptApi;

export {
  promptApi,
  useGetPromptQuery,
  useCreatePromptMutation,
  useCopyPromptMutation,
  useCreatePromptVersionMutation,
  useSavePromptVersionWithDataMutation,
  useGetPromptVersionsQuery,
};
